<template>
    <page-container data-test="external-lists-list">
        <page-header> External Lists </page-header>

        <external-list-list-filters
            v-if="totalCount > 0 || !isEqual(defaultFilters(), fetchParams)"
            v-model="fetchParams"
            @reset="resetFilters"
            @apply="applyFilters"
        ></external-list-list-filters>

        <div v-if="!loading && !list.length" data-test="external-lists-list-message">
            <vx-box
                color="info"
                v-if="totalCount === 0 && !isEqual(defaultFilters(), fetchParams)"
                class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
            >
                <div class="w-64 mx-auto text-center">No results for current search, try adjusting your filters.</div>
                <vx-button type="button" color="secondary" @click="resetFilters">
                    Reset All <font-awesome-icon widthAuto :icon="faCircleX" />
                </vx-button>
            </vx-box>
            <vx-box
                color="info"
                v-else-if="totalCount === 0"
                class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
            >
                No external lists yet.
            </vx-box>
        </div>

        <vx-table v-if="list.length" rowBreakpoint="md" class="grid-cols-5-auto">
            <vx-table-row isHeader>
                <div class="hidden lg:block">Name</div>
                <div>Contacts</div>
                <div>Vendor</div>
                <div>Created</div>
            </vx-table-row>

            <external-list-row
                v-for="(externalList, index) in list"
                :external-list="list[index]"
                :key="list[index].id"
            ></external-list-row>
        </vx-table>

        <div v-if="list.length" class="mt-8 flex justify-between w-full gap-10">
            <vx-button
                v-if="fetchParams.cursor"
                @click="loadData()"
                color="muted-dark"
                size="xl"
                class="grow-0"
                data-test="load-more-btn"
                :loading="loading"
            >
                Load More
                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
            </vx-button>
            <div class="text-lg grow-0 ml-auto font-normal text-slate-600">{{ list.length }}/{{ totalCount }}</div>
        </div>
    </page-container>
</template>
<script setup>
import PageContainer from '~/components/general/PageContainer.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import { VxBox, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { faCircleX, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ExternalListListFilters from './ExternalListListFilters.vue';
import ExternalListRow from './ExternalListRow.vue';
import { onMounted, ref } from 'vue';
import { externalListsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { isEqual } from '~/utils/helpers';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { useExternalListsContactCount } from '~/composables/useExternalListsContactCount';

const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();

const { fetchExternalListsContactCount } = useExternalListsContactCount();
const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const defaultFilters = () => {
    return {
        query: '',
        per_page: 10,
        cursor: null,
        sort_field: 'id',
        sort_dir: 'desc',
    };
};

const totalCount = ref(0);

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };

    loadData();
};

onMounted(() => {
    resetFilters();
});

const fetchTotalCount = async () => {
    try {
        const response = await externalListsClient.getExternalListsCount(teamId, { ...fetchParams.value });
        totalCount.value = response.data?.count || 0;
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    }
};

const loadData = () => {
    fetchRecords(externalListsClient.getExternalLists(teamId, { ...fetchParams.value })).then((data) => {
        if (data?.length) {
            fetchExternalListsContactCount(data.map((externalList) => externalList.id));
        }
    });
    fetchTotalCount();
};
</script>
